// frontend-react/src/hooks/useAuth.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import client from "../api/client";

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const initializeAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const storedUser = localStorage.getItem("user");

      if (token && storedUser) {
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decoded.exp < currentTime) {
            // Token expirado, intentar refresh
            await handleTokenRefresh();
          } else {
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          }
        } catch (decodeError) {
          // Token inválido o malformado
          console.error("Token validation failed:", decodeError.message);
          logout();
        }
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const handleTokenRefresh = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await client.post("/auth/token/refresh/", {
        refresh: refreshToken,
      });

      const newToken = response.data.access;
      localStorage.setItem("authToken", newToken);

      // Obtener usuario actual
      const userResponse = await client.get("/users/current/");
      setUser(userResponse.data);
      localStorage.setItem("user", JSON.stringify(userResponse.data));
      setIsAuthenticated(true);

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return false;
    }
  };

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (authData) => {
    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("refreshToken", authData.refresh_token);
    localStorage.setItem("user", JSON.stringify(authData.user));

    setUser(authData.user);
    setIsAuthenticated(true);
    
    // Force page reload to update header
    window.location.href = "/";
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");

    setUser(null);
    setIsAuthenticated(false);
    navigate("/");
  };

  const hasRole = (role) => {
    return user?.profile?.role === role;
  };

  const canEdit = () => {
    return user?.profile?.role === "admin" || user?.profile?.role === "editor" || user?.is_superuser;
  };

  return {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    hasRole,
    canEdit,
    refreshToken: handleTokenRefresh,
  };
};
